import React, {
  ReactElement,
  useEffect,
  MutableRefObject,
  useState,
} from 'react';
import { GameEngine } from 'react-game-engine';
import Matter from 'matter-js';
import type YouTubePlayer from 'youtube-player';
import { useAnimation } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { Settings, Session } from '../../../../models';
import prepareLoadouts from './loadouts';
import { getWindowDims, getCurrentPath } from '../../../../helpers';
import Recursor from './engine/renderers/Recursor';
import { EVENT_TYPES } from '../../constants';
import PATHS from '../../../../constants/paths';
import events from '../../events';

interface GameForegroundProps {
  gameMode: string;
  settings: Settings;
  session: Session;
  videoPlayer: YouTubePlayer;
  gameEngineRef: MutableRefObject<GameEngine>;
}

function GameForeground({
  gameMode,
  settings,
  session,
  videoPlayer,
  gameEngineRef,
}: GameForegroundProps): ReactElement {
  const engine = Matter.Engine.create();
  const windowDims = getWindowDims();
  const currentPath = getCurrentPath();
  const [running, setRunning] = useState(
    !!gameMode && currentPath === PATHS.arcade
  );

  // Handlers
  const handleDispatch = (event) => {
    if (event.type === EVENT_TYPES.stopGame) gameEngineRef.current?.stop();
    else if (event.type === EVENT_TYPES.swapEntities)
      gameEngineRef.current?.swap(event.detail.entities);
  };

  // Prepare systems and entities
  const a0 = useAnimation();
  const a1 = useAnimation();
  const a2 = useAnimation();
  const a3 = useAnimation();
  const a4 = useAnimation();
  const animates = [a0, a1, a2, a3, a4];
  const navigate = useNavigate();
  const [systems, getEntities] = prepareLoadouts(
    windowDims,
    animates,
    engine,
    videoPlayer,
    navigate,
    settings,
    session,
    gameEngineRef
  )(gameMode);

  // Styling
  const gameForegroundStyle = {
    position: 'absolute' as const,
    top: 0,
    left: '50%',
    marginLeft: -windowDims.w / 2,
    // Dims need to match fixed YouTube query dims
    width: windowDims.w,
    height: windowDims.h,
    overflow: 'hidden', // Needed to hide where notes and bridges load
  };

  useEffect(() => {
    const newEntities = getEntities();

    if (currentPath !== PATHS.arcade) {
      // Make sure game is stopped when not supposed to run
      if (running) {
        gameEngineRef.current?.dispatch(events.stopGame());
        setRunning(false);
      }

      // Clear finished game stuff
      Matter.Composite.clear(engine.world, false, true);
      Matter.Engine.clear(engine);
      gameEngineRef.current?.dispatch(events.swapEntities(newEntities)); // Needed to wipe state before game starts
    } else {
      // Load entities here since `gameMode` unclear till in `Arcade`
      gameEngineRef.current?.dispatch(events.swapEntities(newEntities));
      setRunning(true);
    }
  }, [currentPath]);

  return (
    <div style={gameForegroundStyle}>
      <GameEngine
        renderer={Recursor}
        entities={getEntities()}
        systems={systems}
        onEvent={handleDispatch}
        running={running}
        ref={gameEngineRef}
      />
    </div>
  );
}

export default GameForeground;
